import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Gift, Leaf, Plus } from "lucide-react";
import { toast } from "sonner";
import { Header } from "./Header";
import { Footer } from "./Footer";
import OptionPickerModal from "./OptionPickerModal";
import { HolidayFall } from "./holiday/HolidayFall";
import { useHoliday, THEME_STYLE } from "../lib/holiday";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

const DealCard = ({ d, s, onAdd, loggedIn, added }) => (
  <div data-testid={`holiday-deal-${d.id}`} className="group overflow-hidden rounded-3xl bg-white border border-black/5 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-[transform,box-shadow] duration-300">
    <div className="relative aspect-[4/3] bg-brand-cream overflow-hidden">
      {d.image && <img src={d.image} alt={d.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />}
      {d.regular_price > d.deal_price && (
        <span className={`absolute top-3 left-3 rounded-full px-3 py-1 text-[11px] font-black uppercase tracking-[0.1em] ${s.button}`}>
          Save {money(d.regular_price - d.deal_price)}
        </span>
      )}
    </div>
    <div className="p-5">
      <p className="font-display font-black text-[18px] leading-tight text-brand-charcoal">{d.name}</p>
      {d.note && <p className={`mt-1 text-[12px] font-bold ${s.accentText}`}>{d.note}</p>}
      {d.description && <p className="mt-1.5 text-[13px] text-brand-charcoal/60 line-clamp-2">{d.description}</p>}
      <div className="mt-4 flex items-end justify-between gap-3">
        <p>
          {d.regular_price > d.deal_price && <span className="mr-2 text-[13px] text-brand-charcoal/40 line-through">{money(d.regular_price)}</span>}
          <span data-testid={`holiday-deal-price-${d.id}`} className="font-display font-black text-[22px] text-brand-charcoal">{money(d.deal_price)}</span>
          {d.unit_label && <span className="text-[12px] text-brand-charcoal/50"> / {d.unit_label}</span>}
        </p>
        {loggedIn ? (
          <button type="button" onClick={() => onAdd(d)} data-testid={`holiday-deal-add-${d.id}`} className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2.5 text-[11px] font-bold uppercase tracking-[0.1em] transition-colors ${s.button}`}>
            {added ? <Check className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />} {added ? "Added" : "Add"}
          </button>
        ) : (
          <Link to="/register" state={{ from: "/holiday", gate: "menu" }} data-testid={`holiday-deal-signup-${d.id}`} className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2.5 text-[11px] font-bold uppercase tracking-[0.1em] transition-colors ${s.button}`}>
            Sign up to order
          </Link>
        )}
      </div>
    </div>
  </div>
);

export default function HolidayDealsPage() {
  const h = useHoliday();
  const { user } = useAuth();
  const { menu, addItem } = useCart();
  const [picker, setPicker] = useState(null);
  const [added, setAdded] = useState({});
  const loggedIn = user && typeof user === "object";
  const agency = user?.role === "agency";

  const onAdd = (d) => {
    const item = (menu?.items || []).find((i) => i.id === d.id);
    if (item?.options?.length) { setPicker(item); return; }
    addItem(d.id, 1);
    setAdded((a) => ({ ...a, [d.id]: true }));
    toast.success(`${d.name} added to your cart`);
  };

  if (!h) {
    return (
      <div className="min-h-screen flex flex-col bg-brand-cream"><Header />
        <main className="flex-1 flex items-center justify-center p-6 pt-32 text-center" data-testid="holiday-none">
          <div><p className="font-display font-black text-3xl text-brand-charcoal">No holiday deals right now</p>
            <Link to="/menu" className="mt-4 inline-flex items-center gap-2 text-brand-teal font-bold">See our menu <ArrowRight className="h-4 w-4" /></Link></div>
        </main><Footer /></div>
    );
  }
  const s = THEME_STYLE[h.active];
  const Icon = h.active === "christmas" ? Gift : Leaf;
  return (
    <div className="min-h-screen flex flex-col bg-white" data-testid="holiday-deals-page">
      <Header />
      <HolidayFall />
      <main className="flex-1">
        <section className={`pt-32 sm:pt-36 pb-12 ${s.soft}`}>
          <div className="max-w-6xl mx-auto px-5 sm:px-8">
            <p className={`flex items-center gap-2 text-[11px] font-black uppercase tracking-[0.18em] ${s.accentText}`}><Icon className="h-4 w-4" /> Limited-time holiday pricing</p>
            <h1 className="mt-3 font-display font-black text-4xl sm:text-5xl lg:text-6xl text-brand-charcoal leading-[1.02]">{h.page_title}</h1>
            <p className="mt-4 max-w-xl text-base text-brand-charcoal/70">{h.page_intro}</p>
            {agency && <p data-testid="holiday-agency-note" className="mt-4 max-w-xl text-[13px] text-brand-charcoal/60">Agency accounts pay by Purchase Order at agency pricing, so holiday sale prices apply to retail orders only.</p>}
          </div>
        </section>
        <section className="max-w-6xl mx-auto px-5 sm:px-8 py-12">
          {h.deals.length === 0 ? (
            <p data-testid="holiday-no-deals" className="text-brand-charcoal/60">Holiday deals are coming soon. <Link to="/quote" className="text-brand-teal font-bold">Ask about holiday party catering</Link>.</p>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {h.deals.map((d) => {
                // Agencies pay their PO price; show what they'll actually be charged.
                const mi = agency && (menu?.items || []).find((i) => i.id === d.id);
                const shown = mi ? { ...d, regular_price: mi.price, deal_price: mi.price } : d;
                return <DealCard key={d.id} d={shown} s={s} onAdd={onAdd} loggedIn={loggedIn} added={added[d.id]} />;
              })}
            </div>
          )}
          {loggedIn && (
            <Link to="/cart" data-testid="holiday-go-cart" className="mt-10 inline-flex items-center gap-2 rounded-full bg-brand-charcoal text-white px-6 py-3.5 text-[12px] font-bold uppercase tracking-[0.12em] hover:bg-brand-teal transition-colors">
              Go to cart <ArrowRight className="h-4 w-4" />
            </Link>
          )}
        </section>
      </main>
      <Footer />
      <OptionPickerModal
        item={picker}
        isOpen={Boolean(picker)}
        onClose={() => setPicker(null)}
        onConfirm={({ perPanSelections }) => {
          for (const sel of perPanSelections || [{}]) addItem(picker.id, 1, sel);
          setAdded((a) => ({ ...a, [picker.id]: true }));
          setPicker(null);
        }}
        perPanEnabled
      />
    </div>
  );
}
